<template>
    <reconciliation-step-template
        :nextStep="nextStep"
        :previousStep="previousStep"
        @previous="$emit('previous')"
        :loading="loading"
    >
        <template slot="header">
            <h2 class="mb-0">{{ $l10n.__('Import de fichier CSV') }}</h2>
            <p class="fs-3 gray-80 mt-2">
                {{
                    $l10n.__(
                        'Importez régulièrement votre relevé bancaire afin de vous permettre de pointer vos différentes opérations bancaires avec vos documents SELLSY.',
                    )
                }}
            </p>
        </template>
        <template slot="content">
            <div v-loading="loading">
                <el-form
                    ref="step1Form"
                    label-position="top"
                    :model="formStep"
                    :inline-message="true"
                    label-width="120px"
                >
                    <p class="fs-3 semibold mt-4">1 - {{ $l10n.__('Votre compte bancaire') }}</p>
                    <el-form-item
                        v-if="bankAccounts.length > 0"
                        label=""
                        prop="bankAccount"
                        :error="errors.get('bankAccount')"
                        class="mb-2"
                    >
                        <select-input
                            mode="edit"
                            v-model="formStep.bankAccount"
                            :options="bankAccounts"
                            idColumn="id"
                        />
                    </el-form-item>
                    <p v-else class="red">{{ $l10n.__('Pas de compte bancaire') }}</p>

                    <a href="/?_f=bankAccountPreferences&action=bankInfo" class="link">{{
                        $l10n.__('Liste des comptes bancaires')
                    }}</a>

                    <div v-if="displayAccountInputs" class="my-2">
                        <el-form-item
                            :label="$t.__('common.labels.currency')"
                            prop="currencyId"
                            :error="errors.get('currencyId')"
                            class="mb-2"
                        >
                            <select-input
                                mode="edit"
                                v-model="formStep.currencyId"
                                :options="currencies"
                                idColumn="id"
                                labelColumn="name"
                                :filterable="true"
                            />
                        </el-form-item>
                    </div>

                    <p class="fs-3 semibold mt-4">
                        2 - {{ $l10n.__('Définissez le solde de votre compte') }}
                    </p>
                    <el-form-item label="" prop="balance" :error="errors.get('balance')">
                        <amount-input
                            mode="edit"
                            v-model="formStep.balance"
                            :placeholder="
                                $t.__(
                                    'bankreconciliation.importsteps.ReconciliationStep1.exemple_placeholder',
                                )
                            "
                            :symbol="activeCurrency ? activeCurrency.symbol : ''"
                        />
                    </el-form-item>

                    <p class="fs-3 semibold mt-4 mb-0">
                        3 - {{ $l10n.__('Importez votre relevé bancaire') }}
                    </p>
                    <p class="fs-3 gray-80 mt-1">
                        {{ $l10n.__('Le relevé bancaire doit être au format CSV.') }}
                    </p>
                    <div class="upload-container one-item prelative p-4">
                        <el-form-item
                            label=""
                            prop="file"
                            :error="errors.get('file')"
                            class="mb-0 upload-item"
                        >
                            <el-upload
                                action=""
                                ref="upload"
                                :limit="1"
                                :auto-upload="false"
                                :on-change="updateFile"
                            >
                                <CompassButton
                                    :label="
                                        $t.__(
                                            'bankreconciliation.importsteps.ReconciliationStep1.import_document',
                                        )
                                    "
                                    type="primary"
                                />
                                <!-- <div slot="tip" class="el-upload__tip">csv files with a size less than 500kb</div> -->
                            </el-upload>
                        </el-form-item>
                    </div>
                </el-form>

                <bank-form
                    v-if="displayBankForm"
                    @close="toggleBankForm"
                    :default-bank-code="1"
                    :bank-codes="[]"
                />
            </div>
        </template>
        <template slot="footer">
            <CompassButton
                :label="$t.__('common.actions.continue')"
                type="primary"
                :disabled="bankAccounts.length == 0 || loading"
                @click="goToNextStep"
                v-if="nextStep"
            />
        </template>
    </reconciliation-step-template>
</template>

<script>
import FormMixin from 'mixins/FormMixins';
import ReconciliationStepTemplate from './ReconciliationStepTemplate.vue';
import BankReconciliationImport from 'api/bankreconciliationImport';
import BankForm from 'vcomponents/preferences/BankForm.vue';
import AmountInput from 'vcomponents/inputs/AmountInput.vue';
import SelectInput from 'vcomponents/inputs/SelectInput.vue';

export default {
    mixins: [FormMixin],
    props: {
        previousStep: {
            type: [Boolean, String],
            required: false,
            default: false,
        },
        nextStep: {
            type: [Boolean, String],
            required: false,
            default: false,
        },
    },
    data() {
        return {
            csv: [],
            bankAccounts: [],
            currencies: [],
            formStep: {
                bankAccount: '',
                balance: '',
                file: '',
                currencyId: '1',
            },
            loading: true,
            displayBankForm: false,
        };
    },
    created() {
        BankReconciliationImport.getImportForm().then((response) => {
            this.bankAccounts = response.accounts;
            this.formStep.bankAccount = this.bankAccounts.length > 0 ? this.bankAccounts[0].id : '';
            this.currencies = Object.values(response.currencies);
            this.loading = false;
        });
    },
    computed: {
        activeBankAccount() {
            return this.bankAccounts.length > 0
                ? this.bankAccounts.find(
                      (bankaccount) => bankaccount.id == this.formStep.bankAccount,
                  )
                : false;
        },
        displayAccountInputs() {
            return this.activeBankAccount && this.activeBankAccount.currencyId == null;
        },
        activeCurrency() {
            if (this.currencies.length > 0) {
                if (this.displayAccountInputs) {
                    return this.currencies.find(
                        (currency) => currency.id == this.formStep.currencyId,
                    );
                } else {
                    return this.currencies.find(
                        (currency) => currency.id == this.activeBankAccount.currencyId,
                    );
                }
            }
            return false;
        },
    },
    methods: {
        updateFile(file) {
            this.formStep.file = file;
        },
        importFile() {
            let formData = new FormData();
            formData.append('file', this.formStep.file.raw);
            formData.append('balance', this.formStep.balance);
            formData.append('account_id', parseInt(this.formStep.bankAccount));
            formData.append('currency_id', parseInt(this.formStep.currencyId));

            BankReconciliationImport.upload(formData)
                .then((response) => {
                    this.$emit('update-import-id', response.id);
                    response.skip_config_and_template
                        ? this.$emit('next', 'bankreconciliation-step6')
                        : this.$emit('next');
                })
                .catch((error) => {
                    this.errors.setErrors(error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        goToNextStep() {
            this.loading = true;
            this.submitForm('step1Form');
        },
        save() {
            this.importFile();
        },
        toggleBankForm() {
            this.displayBankForm = !this.displayBankForm;
        },
    },
    components: {
        ReconciliationStepTemplate,
        BankForm,
        AmountInput,
        SelectInput,
    },
};
</script>

<style scoped lang="scss">
.upload-container {
    border: 1px #dae2e6 solid;
    border-style: dashed;
    height: 96px;
    .upload-item {
        width: 200px;
        margin: auto;
        text-align: center;
    }
}
</style>
